import { Popover, type PopoverOrigin } from '@mui/material';
import type { ConferenceFilter, TeamConferenceFilter } from '@on3/api';
import { useSite } from '@on3/ui-lib/index';
import { slugify } from '@on3/ui-lib/utils/slugify';
import clsx from 'clsx';
import { Arrow } from 'components/Svg/Arrow';
import { PrimaryButton } from 'components/UI/Buttons/PrimaryButton';
import { Text } from 'components/UI/Typography/Text';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useCallback, useMemo } from 'react';

import styles, { night, on3Primary } from './Filter.module.scss';

// TODO: we need to clean up the extends
interface IFilterItem {
  /** @format int32 */
  key: number;
  name?: string | null;
  abbreviation?: string | null;
  displayName?: string;
}

// duplicated from /apps/public-webapp/components/Db/Filters/FilterTeams.tsx
interface IFilter {
  /**Name of query param you want to target.*/
  name: string;
  /**List of (key,name) pairs. */
  // TODO: there is a lot here. We really need to clean this up.
  list?: IFilterItem[] | null;
  /**Currently selected item in list. */
  selected?: string;
  /**Replaces list for teams with added filtering by conference*/
  teams?: TeamConferenceFilter[];
  /**Adds className to only the filter button that triggers pop-over list.*/
  buttonClassName?: string;
  /**Choose property to display in url default uses name */
  urlDisplay?: keyof IFilterItem;
  clearQuery?: boolean;
  fillColor?: string;
  pathname?: string;
}

const anchorOrigin: PopoverOrigin = {
  vertical: 'bottom',
  horizontal: 'center',
};

const transformOrigin: PopoverOrigin = {
  vertical: 'top',
  horizontal: 'center',
};

//Once endpoint is updated this can be removed and simply pass to list
/**Takes list of strings and returns key value pairs */
export const makeList = (list: string[] | number[]) => {
  return list.map((item: string | number, index: number) => {
    return { key: index, name: `${item}` };
  });
};

/**Sets conference abbreviation to name and name to fullName for filter component */
export const makeConferenceList = (list: ConferenceFilter[]) => {
  return list.map((item: ConferenceFilter) => {
    return {
      key: item.key,
      name: item.abbreviation,
    };
  });
};

const formatName = (itemName?: string | null) => {
  if (itemName?.includes('-')) {
    return itemName.replace('-', ' ');
  }

  return itemName;
};

const formatUrl = (itemName?: string) => {
  if (itemName?.includes('-')) {
    return itemName.replace('-', ' ').toLowerCase();
  }

  return itemName?.toLowerCase();
};

export const Filter = ({
  name,
  list,
  selected,
  buttonClassName,
  urlDisplay = 'name',
  clearQuery = false,
  fillColor,
  pathname,
}: IFilter) => {
  const router = useRouter();
  const { position, page } = router.query;
  const { currentSite } = useSite();
  const primaryColor =
    currentSite?.linkColor || currentSite?.primaryColor || on3Primary;
  const isPosition = name === 'position';
  const isDefault = name.includes('All') || (isPosition && !position);
  const isSport = name === 'sport' ?? false;
  const [anchorEl, setAnchorEl] = React.useState(null);
  const lowerCaseSelected = selected?.toLowerCase();
  const selection = useMemo(() => {
    let formatSelected = selected;

    if (selected?.includes('-')) {
      formatSelected = selected.replace('-', ' ');
    }

    if (isPosition && !isDefault) {
      formatSelected = formatSelected?.toUpperCase();
    }

    return formatSelected;
  }, [isDefault, isPosition, selected]);

  const handleClick = useCallback((event: any) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  /**returns basePath removing query string */
  const basePath = useMemo(() => {
    if (router.asPath.includes('?')) {
      return router.asPath.split('?')[0];
    }

    return router.asPath;
  }, [router.asPath]);

  /**Checks for key -1 is for showing all thus removing query otherwise updates query param value */
  const getHref = useCallback(
    (item: any) => {
      const chosenPathname = pathname ?? router.pathname;

      if (item.key === -1) {
        const newHref = {
          pathname: chosenPathname,
          query: {
            ...router.query,
          },
        };

        delete newHref.query[name];

        return newHref;
      }

      if (clearQuery) {
        const urlParam = item?.[urlDisplay]?.toString().toLowerCase();

        return `${basePath}?${name}=${urlParam}`;
      }

      const newHref = {
        pathname: chosenPathname,
        query: {
          ...router.query,
          [name]: slugify(item?.[urlDisplay]),
        },
      };

      if (parseInt(`${page}`) > 1) {
        newHref.query.page = '1';
      }

      if (isSport) {
        delete newHref.query.position;

        return newHref;
      }

      return newHref;
    },

    [
      basePath,
      clearQuery,
      isSport,
      name,
      page,
      pathname,
      router.pathname,
      router.query,
      urlDisplay,
    ],
  );

  return (
    <div className={styles.filterWrapper}>
      <PrimaryButton
        aria-describedby={id}
        className={clsx(styles.filterButton, buttonClassName)}
        fullWidth={false}
        onClick={handleClick}
        style={{ color: fillColor || primaryColor }}
        variant="text"
      >
        {selection}
        <Arrow className={styles.arrow} fill={fillColor || primaryColor} />
      </PrimaryButton>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={anchorOrigin}
        id={id}
        onClose={handleClose}
        open={open}
        transformOrigin={transformOrigin}
      >
        <Text className={styles.wrapper}>
          {list?.map((item) => {
            const isActive =
              lowerCaseSelected === formatUrl(item?.[urlDisplay]?.toString()) ||
              (isDefault && item.key === -1) ||
              item.displayName?.toLowerCase() === lowerCaseSelected;
            const itemStyle = {
              color: isActive ? night : fillColor || primaryColor,
            };

            return (
              <Link
                href={getHref(item)}
                key={item.key}
                onClick={handleClose}
                passHref
              >
                <Text
                  className={styles.link}
                  color="primary"
                  style={itemStyle}
                  variant="subtitle1"
                >
                  {formatName(item.displayName ?? item.name)}
                </Text>
              </Link>
            );
          })}
        </Text>
      </Popover>
    </div>
  );
};

export default Filter;
